import { useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchConnectLink,
  savePracticeDomain,
  saveConnectLink,
  takenSlugError,
} from "../services/formLinkService";
import type { ConnectLink } from "../types/brand.types";

export type FormLinkStatus = "loading" | "ready" | "failed";

/**
 * What a claim came back with.
 *
 * Three outcomes rather than a boolean, because the middle one is the whole
 * point of the record: `rejected` is the server saying the slug belongs to
 * somebody else, and it carries the sentence to print under the field. A
 * `failed` is everything else — it has already been said in a toast, and the
 * field is not at fault.
 */
export type FormLinkClaim =
  | { outcome: "saved"; link: ConnectLink }
  | { outcome: "rejected"; message: string }
  | { outcome: "failed" };

export interface FormLinkState {
  /** The link already claimed, or `null` where none has been. */
  link: ConnectLink | null;
  status: FormLinkStatus;
  reload: () => void;
  /** True while a claim is with the server; the Save button reads it. */
  saving: boolean;
  claim: (slug: string) => Promise<FormLinkClaim>;
  /**
   * Tells the pre-consultation service which subdomain to serve this
   * workspace's client links on.
   *
   * The second half of what Save does, and the half that can fail on its own.
   * Called whether or not a claim was sent first — which is what backfills a
   * workspace that named itself before this call existed, and what makes a
   * second press retry one that failed. Deliberately not part of `claim`: a
   * claim that reported itself failed because of this call would be lying
   * about a slug the backend has already stored.
   *
   * Save is the only thing that calls it. Copy reaches the clipboard and never
   * the server.
   *
   * Never throws — it reports its own failure in a toast. It answers whether
   * the record is stored, which is what decides whether the box locks: a name
   * whose second record is missing is one the Save button still has work to
   * do about.
   */
  registerDomain: (slug: string) => Promise<boolean>;
}

export const FORM_LINK_QUERY_KEY = ["brand", "connect-link"] as const;

/**
 * The practitioner's link, read on open and claimed on demand.
 *
 * No toast on success from here. A claim is only half of what Save does — the
 * subdomain still has to be registered after it — so the sentence that says
 * the link is saved belongs to whoever sequenced the two, not to the first of
 * them. See `BrandFormTab`.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useFormLink(): FormLinkState {
  const getToken = useTokenGetter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const query = useQuery({
    queryKey: FORM_LINK_QUERY_KEY,
    queryFn: ({ signal }) => fetchConnectLink(getToken, signal),
  });

  /**
   * The slug this session has already registered, so a run of Save presses
   * sends one call rather than one each.
   *
   * A ref and not state: nothing on the screen changes when it is set, and it
   * is only ever read inside the callback below. Deliberately not persisted —
   * a fresh page load registering once more is the cheap half of what makes
   * this self-healing, and there is no way from here to know what the service
   * already holds.
   */
  const registered = useRef<string | null>(null);

  const registerDomain = useCallback(
    async (slug: string): Promise<boolean> => {
      if (!slug) return false;
      // Already stored this session: nothing to send, and the answer is still
      // yes, so a run of presses neither re-sends nor reads as a failure.
      if (registered.current === slug) return true;

      try {
        await savePracticeDomain(getToken, slug);
        registered.current = slug;
        return true;
      } catch (error) {
        console.error("practice domain failed to register:", error);
        // In the terms of the thing that will actually not work. The link
        // itself is claimed either way, and saying "could not save your link"
        // about a name the backend has just stored would be false.
        toast.warning(
          "Pre-consultation forms are not ready yet",
          "Your form link is saved and ready to share. Press Save again to finish setting up the pre-consultation form."
        );
        return false;
      }
    },
    [getToken, toast]
  );

  const claim = useCallback(
    async (slug: string): Promise<FormLinkClaim> => {
      setSaving(true);
      try {
        const link = await saveConnectLink(getToken, slug);
        // Written straight into the cache: the response is the whole record,
        // so a refetch would only ask for what is already in hand.
        queryClient.setQueryData<ConnectLink>(FORM_LINK_QUERY_KEY, link);

        // The pre-consultation service is told this name too, but not from
        // here: see `registerDomain`, which the Save button calls next. Folding
        // it into this call would make one failure of two records, and a slug
        // the backend has stored would be reported as unsaved.
        return { outcome: "saved", link };
      } catch (error) {
        console.error("connect link failed to save:", error);

        // The one failure that belongs under the field rather than over the
        // screen — somebody else has the slug, and the fix is to type another.
        // It can arrive even when the availability check said the slug was
        // free: that answer was true when it was given, and this is the one
        // that decides.
        const taken = takenSlugError(error);
        if (taken) return { outcome: "rejected", message: taken };

        toast.error(
          "Could not save your link",
          error instanceof ApiError
            ? error.message
            : "We could not reach the server. Check your connection and try again."
        );
        return { outcome: "failed" };
      } finally {
        setSaving(false);
      }
    },
    [getToken, queryClient, toast]
  );

  return {
    link: query.data ?? null,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    reload: () => {
      void query.refetch();
    },
    saving,
    claim,
    registerDomain,
  };
}

export default useFormLink;
